import React, { useEffect, useState } from "react";
import { staffApi, apiError } from "../../api/lab";
import { useApp } from "../../AppProvider";
import { Modal, Button, Field } from "../../Components/UI/ui";
import { STAFF_TYPES, EMPLOYMENT_TYPES, cap, fileToImage, toDateInput } from "../../lib/lab";

const phone10 = (v) => String(v || "").replace(/\D/g, "").slice(-10);

// ── Profile fields (PUT /labStaff/:staffId) — used for "My profile" and member edit
export const ProfileFields = ({ f, set, showType = true }) => (
  <div className="lab-grid cols-3">
    <Field label="First name"><input className="lab-input" value={f.firstName} onChange={(e) => set({ firstName: e.target.value })} /></Field>
    <Field label="Last name"><input className="lab-input" value={f.lastName} onChange={(e) => set({ lastName: e.target.value })} /></Field>
    <Field label="Phone (10 digits)"><input className="lab-input" value={f.phone} onChange={(e) => set({ phone: e.target.value })} /></Field>
    <Field label="Email"><input className="lab-input" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
    {showType && (
      <Field label="Job title">
        <select className="lab-select" value={f.staffType} onChange={(e) => set({ staffType: e.target.value })}>
          {STAFF_TYPES.map((t) => <option key={t} value={t}>{cap(t)}</option>)}
        </select>
      </Field>
    )}
    <Field label="Employment">
      <select className="lab-select" value={f.employmentType} onChange={(e) => set({ employmentType: e.target.value })}>
        <option value="">—</option>
        {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{cap(t)}</option>)}
      </select>
    </Field>
    <Field label="Qualification" hint="Printed under the signature on reports"><input className="lab-input" placeholder="MD (Pathology)" value={f.qualification} onChange={(e) => set({ qualification: e.target.value })} /></Field>
    <Field label="Registration number" hint='Printed as "Reg. No."'><input className="lab-input" placeholder="MMC-1234" value={f.registrationNumber} onChange={(e) => set({ registrationNumber: e.target.value })} /></Field>
    <Field label="Date of joining"><input type="date" className="lab-input" value={f.dateOfJoining} onChange={(e) => set({ dateOfJoining: e.target.value })} /></Field>
  </div>
);

export const profileToForm = (m) => ({
  firstName: m?.name?.firstName || "", lastName: m?.name?.lastName || "",
  phone: m?.contact?.phoneNumber?.phoneNumber || "", email: m?.contact?.email || "",
  staffType: m?.staffType || "general", employmentType: m?.employmentType || "",
  qualification: m?.qualification || "", registrationNumber: m?.registrationNumber || "",
  dateOfJoining: m?.dateOfJoining ? toDateInput(m.dateOfJoining) : "",
});

export const profileToBody = (f) => ({
  name: { firstName: f.firstName.trim(), lastName: f.lastName.trim() },
  contact: {
    email: f.email.trim() || undefined,
    phoneNumber: phone10(f.phone).length === 10 ? { countryCode: "+91", phoneNumber: phone10(f.phone) } : undefined,
  },
  staffType: f.staffType, employmentType: f.employmentType || undefined,
  qualification: f.qualification.trim(), registrationNumber: f.registrationNumber.trim(),
  dateOfJoining: f.dateOfJoining ? new Date(f.dateOfJoining).toISOString() : undefined,
});

// ── Edit an existing member's profile
export const EditMemberModal = ({ open, onClose, member, onSaved }) => {
  const { toast } = useApp();
  const [f, setF] = useState(profileToForm(null));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  useEffect(() => { if (open) { setF(profileToForm(member)); setErr(null); } }, [open, member]);

  const save = async () => {
    setErr(null);
    if (!f.firstName.trim()) return setErr("First name is required");
    setBusy(true);
    try {
      const saved = await staffApi.update(member.staffId || member._id, profileToBody(f));
      toast.success("Profile saved");
      onSaved?.(saved);
      onClose();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Edit · ${member?.name?.firstName || ""} ${member?.name?.lastName || ""}`}
      footer={<>{err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}<Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>Save</Button></>}>
      <ProfileFields f={f} set={set} />
    </Modal>
  );
};

// ── Add a new member + login (POST /labStaff)
export const AddMemberModal = ({ open, onClose, roles, onCreated }) => {
  const [f, setF] = useState({ ...profileToForm(null), staffType: "lab_technician", role: roles[roles.length - 1] || "staff", username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [fieldErr, setFieldErr] = useState({});
  const set = (p) => setF((x) => ({ ...x, ...p }));
  useEffect(() => { if (open) { setF({ ...profileToForm(null), staffType: "lab_technician", role: roles[roles.length - 1] || "staff", username: "", password: "" }); setErr(null); setFieldErr({}); } }, [open, roles]);

  const submit = async () => {
    setErr(null); setFieldErr({});
    if (!f.firstName.trim()) return setErr("First name is required");
    if (phone10(f.phone).length !== 10) return setFieldErr({ phone: "10 digits required" });
    if (!f.username.trim()) return setFieldErr({ username: "Required" });
    if (f.password.length < 6) return setFieldErr({ password: "At least 6 characters" });
    if (f.role === "signatory" && (!f.qualification.trim() || !f.registrationNumber.trim())) {
      return setErr("Signatories need a qualification and registration number — both print on reports");
    }
    setBusy(true);
    try {
      const body = {
        ...profileToBody(f),
        phoneNumber: phone10(f.phone),
        email: f.email.trim() || undefined,
        role: f.role, username: f.username.trim(), password: f.password,
      };
      delete body.contact;
      const member = await staffApi.create(body);
      onCreated?.(member, f.password);
      onClose();
    } catch (e) {
      const msg = apiError(e);
      if (/username/i.test(msg)) setFieldErr({ username: msg });
      else if (/phone/i.test(msg)) setFieldErr({ phone: msg });
      else setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Add member"
      footer={<>{err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}<Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={submit}>Create login</Button></>}>
      <div className="lab-section">
        <h4>Person</h4>
        <ProfileFields f={f} set={set} />
        {fieldErr.phone && <div className="lab-danger-text">{fieldErr.phone}</div>}
      </div>
      <div className="lab-section">
        <h4>Login <span className="lab-muted" style={{ fontWeight: 400 }}>they must change the password at first login</span></h4>
        <div className="lab-grid cols-3">
          <Field label="Role">
            <select className="lab-select" value={f.role} onChange={(e) => set({ role: e.target.value })}>
              {roles.map((r) => <option key={r} value={r}>{cap(r)}</option>)}
            </select>
          </Field>
          <Field label="Username" error={fieldErr.username}><input className="lab-input" autoComplete="off" value={f.username} onChange={(e) => set({ username: e.target.value })} /></Field>
          <Field label="Temporary password" error={fieldErr.password}><input className="lab-input" autoComplete="new-password" value={f.password} onChange={(e) => set({ password: e.target.value })} /></Field>
        </div>
      </div>
    </Modal>
  );
};

// ── Give an existing lab user (from another lab) a membership here
export const AddExistingModal = ({ open, onClose, roles, onAdded }) => {
  const [by, setBy] = useState("username");
  const [value, setValue] = useState("");
  const [role, setRole] = useState(roles[roles.length - 1] || "staff");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (open) { setBy("username"); setValue(""); setRole(roles[roles.length - 1] || "staff"); setErr(null); } }, [open, roles]);

  const submit = async () => {
    setErr(null);
    if (!value.trim()) return setErr(`Enter a ${by}`);
    setBusy(true);
    try {
      const body = { role };
      if (by === "username") body.username = value.trim(); else body.phoneNumber = phone10(value);
      const member = await staffApi.addExisting(body);
      onAdded?.(member);
      onClose();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="sm" title="Add existing user"
      footer={<>{err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}<Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={submit}>Add to lab</Button></>}>
      <span className="lab-muted">Someone who already has a login at another Cohesive lab.</span>
      <div className="lab-row">
        <select className="lab-select" value={by} onChange={(e) => setBy(e.target.value)}><option value="username">Username</option><option value="phoneNumber">Phone</option></select>
        <input className="lab-input" style={{ flex: 1 }} value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
      </div>
      <Field label="Role at this lab">
        <select className="lab-select" value={role} onChange={(e) => setRole(e.target.value)}>{roles.map((r) => <option key={r} value={r}>{cap(r)}</option>)}</select>
      </Field>
    </Modal>
  );
};

// ── Temporary password shown once (after create or reset)
export const TempPasswordModal = ({ open, onClose, member, password }) => {
  const { toast } = useApp();
  const copy = async () => { try { await navigator.clipboard.writeText(password); toast.success("Copied"); } catch { /* clipboard blocked */ } };
  return (
    <Modal open={open} onClose={onClose} size="sm" title="Temporary password" footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <span>Share this with <b>{member?.name?.firstName} {member?.name?.lastName}</b> (username <code>{member?.login?.username}</code>). It is shown only once; they'll be asked to change it at first login.</span>
      <div className="lab-row">
        <code style={{ fontSize: 20, padding: "8px 12px", background: "#f1f3f4", borderRadius: 6, flex: 1 }}>{password}</code>
        <Button size="sm" onClick={copy}>Copy</Button>
      </div>
    </Modal>
  );
};

// ── Signature upload (POST /labStaff/:staffId/signature)
export const SignatureUpload = ({ staffId, hasSignature, onUploaded }) => {
  const { toast } = useApp();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);
  const [broken, setBroken] = useState(false);

  const upload = async () => {
    setBusy(true);
    try {
      const image = await fileToImage(file, 1024 * 1024);
      await staffApi.uploadSignature(staffId, image);
      toast.success("Signature saved");
      setFile(null); setBust(Date.now()); setBroken(false);
      onUploaded?.();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };

  return (
    <div className="lab-row" style={{ alignItems: "flex-start" }}>
      <div style={{ width: 200, height: 80, border: "1px dashed #989898", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", overflow: "hidden" }}>
        {(hasSignature || bust) && !broken
          ? <img alt="signature" src={staffApi.signatureUrl(staffId, bust)} style={{ maxWidth: "100%", maxHeight: "100%" }} onError={() => setBroken(true)} />
          : <span className="lab-muted" style={{ fontSize: 12 }}>No signature</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input type="file" accept="image/png,image/jpeg" className="lab-input" style={{ paddingTop: 7 }} onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <div className="lab-row">
          <Button size="sm" variant="primary" disabled={!file} loading={busy} onClick={upload}>Upload</Button>
          <span className="lab-muted" style={{ fontSize: 12 }}>PNG/JPEG ≤ 1 MB. Without one, reports print name + qualification only.</span>
        </div>
      </div>
    </div>
  );
};
